// @owners { team: patient-health }
import { Button, Column, Row, useScreenSize } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { Label, Text, palette, spacing } from '@alto/pocky';
import { PrescriptionStatusMap } from '@alto/scriptdash/alto/patient_app/types/v1/prescription_status';
import React from 'react';
import styled from 'styled-components';
import styledNative from 'styled-components/native';
import { openModal } from '~shared/actions/modal';
import { PRESCRIPTION_STATES } from '~shared/constants';
import { ACTIONABLE_PRESCRIPTION_STATES } from '~shared/features/my-meds/constants';
import { formatDateWithYear } from '~shared/helpers/date';
import { formatDeliveryDetails } from '~shared/helpers/delivery';
import { formatQuantity } from '~shared/helpers/helper';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { useDispatchShared } from '~shared/store';
import { type Address, type LightDelivery, type Prescription as PrescriptionT } from '~shared/types/clients';
import { ADD_TO_CART_DISPLAY } from './AddToCart';
import PrescriptionAction from './PrescriptionActionContainer';
import PrescriptionInactive from './PrescriptionInactive';
import { UpcomingDeliveryCard } from '~web/components/UpcomingDeliveryCard';

type PrescriptionProps = {
  readonly address: Address | undefined;
  readonly isSinglePrescription?: boolean;
  readonly prescription: PrescriptionT;
  readonly suppressAction?: boolean;
  readonly upcomingDelivery: LightDelivery | undefined;
};

const PrescriptionContainer = styledNative(Row)<{ activeState: string | undefined }>`
  background-color: ${palette.altoColorsW};
  border: ${spacing.borderWidth} solid ${palette.grey2};
  border-radius: ${spacing.borderRadius};
`;

const PrescriptionAttrs = styledNative(Row)`
  padding: ${spacing.smSpacing};
`;

const PrescriptionAttrContainer = styledNative(Column)<{ isSMScreenOrBigger?: boolean; isMDScreenOrBigger?: boolean }>`
  flex: 50%;
  padding-bottom: ${spacing.smSpacing};
  align-content: space-between;

  ${({ isSMScreenOrBigger }) => isSMScreenOrBigger && `width: auto;min-width: auto;flex: 33%; `}

  ${({ isMDScreenOrBigger }) => isMDScreenOrBigger && `min-height: 4rem;flex-basis: 33%;`}
`;

const PrescriptionDescriptionContainer = styledNative(PrescriptionAttrContainer)`
  flex: 100%;
`;

const PrescriptionArchiveContainer = styledNative(PrescriptionDescriptionContainer)`
  min-height: 2rem;
  padding-bottom: 0;
`;

const PrescriptionAttrText = styled(Text)<{ title?: string | number }>`
  max-width: 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RxActionOrDeliveryInfo = ({
  address,
  isSinglePrescription,
  prescription,
  suppressAction,
  upcomingDelivery,
}: PrescriptionProps) => {
  if (isSinglePrescription) {
    return (
      <PrescriptionAction
        prescription={prescription}
        showAddToCartOnly={suppressAction ? ADD_TO_CART_DISPLAY.NONE : null}
      />
    );
  }

  // Multiple active rx should separately show upcoming delivery info per rx if exists. This is in addition to the
  // one already rendered in MedListRow (collapsed view), which is sufficient only for single active rx.
  const shipmentID = upcomingDelivery?.shipment_id;
  if (shipmentID) {
    const upcomingDeliveryDetails = formatDeliveryDetails(address, upcomingDelivery);

    return (
      <UpcomingDeliveryCard
        shipmentID={shipmentID}
        origin={ORIGIN_NAMES.MED_DETAILS}
        deliveryDetails={upcomingDeliveryDetails}
        isEditable
      />
    );
  }

  return <PrescriptionAction prescription={prescription} />;
};

export const Prescription = ({
  address,
  isSinglePrescription,
  prescription,
  suppressAction,
  upcomingDelivery,
}: PrescriptionProps) => {
  const dispatch = useDispatchShared();
  const { active_state, status } = prescription;
  const isPrescriptionInactive = ![
    PRESCRIPTION_STATES.ACTIVE,
    PRESCRIPTION_STATES.ACTIVE_RENEWED,
    PRESCRIPTION_STATES.NO_REFILLS,
    PRESCRIPTION_STATES.TOO_EARLY,
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  ].includes(active_state);
  const { isSMScreenOrBigger, isMDScreenOrBigger } = useScreenSize();

  const prescriptionAttributes = () => {
    return [
      {
        key: 'fill-qty',
        label: <span>Fill quantity</span>,

        value: (rx: PrescriptionT) => {
          const formattedQuantity = formatQuantity(rx);
          return <PrescriptionAttrText title={formattedQuantity}>{formattedQuantity}</PrescriptionAttrText>;
        },
      },
      {
        key: 'refills-left',
        label: 'Refills left',

        value: (rx: PrescriptionT) => (
          <PrescriptionAttrText title={rx.refills_remaining}>{rx.refills_remaining}</PrescriptionAttrText>
        ),
      },
      {
        key: 'expiration-date',
        label: 'Expires on',

        value: (rx: PrescriptionT) =>
          rx.expiration_date ? (
            <PrescriptionAttrText title={formatDateWithYear(rx.expiration_date || '')}>
              {formatDateWithYear(rx.expiration_date || '')}
            </PrescriptionAttrText>
          ) : (
            <PrescriptionAttrText title="Unavailable">Unavailable</PrescriptionAttrText>
          ),
      },
      {
        key: 'prescribed-date',
        label: 'Prescribed on',

        value: (rx: PrescriptionT) =>
          rx.date_written ? (
            <PrescriptionAttrText title={formatDateWithYear(rx.date_written || '')}>
              {formatDateWithYear(rx.date_written || '')}
            </PrescriptionAttrText>
          ) : (
            <PrescriptionAttrText title="Unavailable">Unavailable</PrescriptionAttrText>
          ),
      },
      {
        key: 'rx-number',
        label: 'Rx number',

        value: (rx: PrescriptionT) => <PrescriptionAttrText title={rx.rx_number}>{rx.rx_number}</PrescriptionAttrText>,
      },
      {
        key: 'prescriber',
        label: 'Prescribed by',

        value: (rx: PrescriptionT) =>
          rx.doctor ? (
            <PrescriptionAttrText title={`${rx.doctor.first_name || ''} ${rx.doctor.last_name || ''}`}>
              {rx.doctor.first_name} {rx.doctor.last_name}
            </PrescriptionAttrText>
          ) : (
            <PrescriptionAttrText title="Unavailable">Unavailable</PrescriptionAttrText>
          ),
      },
    ];
  };

  const openArchiveModal = () => {
    dispatch(openModal('ARCHIVE_PRESCRIPTION_MODAL', { prescription }));
    dispatch(
      sendAnalyticsEvent(
        createEvent(EVENTS.MED_DETAILS__ARCHIVE_CONFIRMATION_VIEWED, {}, { prescriptionId: prescription.id }),
      ),
    );
  };

  return (
    <Column gap="MD">
      <RxActionOrDeliveryInfo
        address={address}
        isSinglePrescription={isSinglePrescription}
        prescription={prescription}
        suppressAction={suppressAction}
        upcomingDelivery={upcomingDelivery}
      />
      <PrescriptionContainer activeState={active_state}>
        <Column>
          {isPrescriptionInactive ? <PrescriptionInactive prescription={prescription} /> : null}

          <PrescriptionAttrs>
            <PrescriptionDescriptionContainer>
              <Label light>Directions</Label>
              <Text>{prescription.sig}</Text>
            </PrescriptionDescriptionContainer>
            {prescriptionAttributes().map(({ key, label, value }) => (
              <PrescriptionAttrContainer
                key={`${prescription.id}-${key}`}
                isSMScreenOrBigger={isSMScreenOrBigger}
                isMDScreenOrBigger={isMDScreenOrBigger}
              >
                <Label light>{label}</Label>
                {value(prescription)}
              </PrescriptionAttrContainer>
            ))}
            {/* @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assignable to parameter of type 'string'. */}
            {ACTIONABLE_PRESCRIPTION_STATES.includes(active_state) && (
              <PrescriptionArchiveContainer>
                <Button
                  onPress={openArchiveModal}
                  small
                  type={status !== PrescriptionStatusMap.OBSOLETED ? 'tertiary' : 'primary'}
                  noPadding={status !== PrescriptionStatusMap.OBSOLETED}
                  label="Archive this prescription"
                />
              </PrescriptionArchiveContainer>
            )}
          </PrescriptionAttrs>
        </Column>
      </PrescriptionContainer>
    </Column>
  );
};
